<template>
  <div
    :class="[
      'UIInputRadio',
      $attrs.class,
      {
        'UIInputRadio--has-errors': hasErrors,
        'UIInputRadio--is-disabled': isDisabled,
      },
    ]"
  >
    <div class="UIInputRadio__input-wrapper">
      <input
        :id="id || name"
        v-bind="$attrs"
        v-model="value"
        :name="name"
        class="UIInputRadio__input"
        type="radio"
        :disabled="isDisabled"
        @keydown.enter.prevent=""
      />
      <UIInputLabel
        v-if="label || $slots['label']"
        class="UIInputRadio__label"
        :for="id || name"
        :isRequired="isRequired"
      >
        <slot name="label">{{ label }}</slot>
      </UIInputLabel>
    </div>

    <!-- Legacy description -->
    <div v-if="description" class="UIInputRadio__description">
      {{ description }}
    </div>
    <!-- New description -->
    <UIInputDescription>
      {{ description }}
    </UIInputDescription>

    <!-- Legacy error -->
    <template v-for="error in errors" :key="error">
      <div class="UIInputRadio__error">
        {{ error }}
      </div>
    </template>

    <!-- New error -->
    <UIInputError v-for="error in errors" :key="error">
      {{ error }}
    </UIInputError>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { computed, toRef, watch } from 'vue'

import { useField } from 'vee-validate'

import UIInputDescription from './UIInputDescription.vue'
import UIInputError from './UIInputError.vue'
import UIInputLabel from './UIInputLabel.vue'

import type * as yup from 'yup'

defineOptions({
  inheritAttrs: false,
})
const props = defineProps({
  label: { type: String, required: false },
  id: { type: String, default: '' },
  name: { type: String, required: true },
  description: { type: String, default: '' },
  modelValue: {
    type: [String, Number],
    default: undefined,
  },
  isDisabled: { type: Boolean, default: false },
  /**
   * yup rule validator for vee-validate
   **/
  validator: {
    type: Object as PropType<typeof yup>,
    default: () => ({}),
  },
  /**
   * Adds an asterisk to the label
   */
  isRequired: {
    type: Boolean,
    default: false,
  },
})

const hasErrors = computed(() => errors.value && errors.value.length > 0)
const { value, errors } = useField(toRef(props, 'name'), props.validator, {
  initialValue: props.modelValue,
})

const emit = defineEmits(['update:modelValue'])
watch(value, (newValue, oldValue) => {
  if (!newValue && oldValue != undefined) return

  emit('update:modelValue', newValue)
})
watch(
  () => props.modelValue,
  (val) => {
    value.value = val
  },
)
</script>

<style scoped lang="scss">
.UIInputRadio {
  --active-color: #{$color-neutral-170};
  --label-color: #{$color-neutral-170};

  display: flex;
  flex-direction: column;
  position: relative;

  &.UIInputRadio--has-errors {
    --border-color: #{$color-error-40};

    .UIInputRadio__input {
      border-color: var(--border-color);
    }
  }

  &.UIInputRadio--is-disabled {
    pointer-events: none;

    .UIInputRadio__input {
      pointer-events: none;
      background-color: $color-white;
    }
  }

  .UIInputRadio__input-wrapper {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem 0.5rem 0;
  }

  .UIInputRadio__label {
    margin-bottom: 0;
    cursor: pointer;
    margin-left: 0.25rem;
  }

  .UIInputRadio__input {
    @include font-size-14px;
    appearance: auto;
    width: 1rem;
    height: 1rem;
    cursor: pointer;
    accent-color: $color-primary-40;
    // padding: 0.5rem 0.75rem;
    // padding-right: 2.25rem;
    // display: block;
    // width: 100%;
    // color: $color-neutral-170;
    // border-radius: 4px;
    // transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    // position: relative;
    // z-index: 1;
  }

  .UIInputRadio__description {
    @include font-size-10px;
    margin-top: 0.25rem;
    color: $color-neutral-70;
  }

  .UIInputRadio__error {
    @include font-size-10px;
    color: $color-error-40;
    margin-top: 0.25rem;
  }

  .UIInputRadio__input-placeholder {
    position: absolute;
    top: 0.5rem;
    left: 0.75rem;
    color: $color-neutral-40;
    z-index: 1;
  }
  .UIInputRadio__input-placeholder--invisible {
    visibility: hidden;
  }
}
</style>
